import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { Account, Payee } from "model";

/**
 * @class WireActivity
 * @classdesc Class definition for wire activity records.
 * @param {OBJECT} record - record from the model to initialize the class
 * @extends BCMSRecord
 * @property {STRING} _id - The wire activity ID.
 * @property {STRING} _referenceNumber - The wire reference number.
 * @property {PAYEE} _payee - The wire payee.
 * @property {BOOLEAN} _notifyPayee - Whether the payee was notified.
 * @property {ACCOUNT} _fundingAccount - The funding account.
 * @property {BCMSCurrency} _amount - The wire amount.
 * @property {STRING} _status - The wire status.
 * @property {STRING} _statusDescription - The wire status description.
 * @property {BCMSDate} _date - The wire date.
 * @property {BCMSDate} _futureWireDate - The future wire date.
 * @property {STRING} _memo - The wire memo.
 * @property {STRING} _notes - Activity notes.
 * @property {STRING} _beneficiaryReference - The beneficiary reference.
 * @property {STRING} _messageToBeneficiary1 - The first beneficiary message.
 * @property {STRING} _messageToBeneficiary2 - The second beneficiary message.
 * @property {STRING} _messageToBeneficiary3 - The third beneficiary message.
 * @property {STRING} _messageToBeneficiary4 - The fourth beneficiary message.
 * @property {STRING} _reasonForPayment - The payment reason.
 * @property {BCMSDate} _recurringEndByDate - The recurring end date.
 * @property {BCMSDate} _recurringWireDate - The next recurring wire date.
 * @property {STRING} _recurringNumberOfTransfers - The recurring transfer count.
 * @property {STRING} _schedulingOption - The scheduling option.
 * @property {STRING} _frequency - The recurrence frequency.
 * @property {STRING} _frequencyType - The recurrence end condition.
 */
export default class WireActivity extends BCMSRecord {
  _id;
  _referenceNumber;
  _payee;
  _notifyPayee;
  _fundingAccount;
  _amount;
  _status;
  _statusDescription;
  _date;
  _futureWireDate;
  _memo;
  _notes;
  _beneficiaryReference;
  _messageToBeneficiary1;
  _messageToBeneficiary2;
  _messageToBeneficiary3;
  _messageToBeneficiary4;
  _reasonForPayment;
  _recurringEndByDate;
  _recurringWireDate;
  _recurringNumberOfTransfers;
  _schedulingOption;
  _frequency;
  _frequencyType;
  _payeeName;
  _wireTime

  constructor(record) {
    super(record)
  }

  get wireFundingAccount() {
    return this._fundingAccount ? this._fundingAccount : Account.getDefaultBrandedAccountByType("checking");
  }

}

WireActivity.define({
  "recordType": "wireActivity",
  "recordIDProp": "id",
  "className": "WireActivity",
  "recordProperties": [
      /** 
       * @name id
       * @instance
       * @property get {NUMBER} - gets the id 
       * @property set {NUMBER} - sets the id 
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("id","_id"),
      /** 
       * @name referenceNumber
       * @instance
       * @property get {STRING} - gets the reference number
       * @property set {STRING} - sets the reference number
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("referenceNumber","_referenceNumber"),
      /** 
       * @name payee
       * @instance
       * @property get {STRING} - gets the payee
       * @property set {STRING} - sets the payee
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToClass("payee","_payee", () => Payee),
      /** 
       * @name notifyPayee
       * @instance
       * @property get {BOOLEAN} - gets the notifyPayee value
       * @property set {BOOLEAN} - sets the notifyPayee value
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToBoolean("notifyPayee", "_notifyPayee"),
   
       /** 
       * @name amount
       * @instance
       * @property get {STRING} - gets the amount
       * @property set {STRING} - sets the amount
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToCurrency("amount","_amount"),
       /** 
       * @name statusDescription
       * @instance
       * @property get {STRING} - gets the statusDescription
       * @property set {STRING} - sets the statusDescription
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("statusDescription","_statusDescription"),
       /** 
       * @name date
       * @instance
       * @property get {STRING} - gets the date
       * @property set {STRING} - sets the date
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToDate("date","_date"),
      /** 
       * @name futureWireDate
       * @instance
       * @property get {STRING} - gets the futureWireDate
       * @property set {STRING} - sets the futureWireDate
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToDate("futureWireDate","_futureWireDate"),
      /** 
       * @name memo
       * @instance
       * @property get {STRING} - gets the memo
       * @property set {STRING} - sets the memo
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("memo","_memo"),
      /** 
       * @name beneficiaryReference
       * @instance
       * @property get {STRING} - gets the beneficiaryReference
       * @property set {STRING} - sets the beneficiaryReference
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("beneficiaryReference","_beneficiaryReference"),
      /** 
       * @name messageToBeneficiary1
       * @instance
       * @property get {STRING} - gets the messageToBeneficiary1
       * @property set {STRING} - sets the messageToBeneficiary1
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("messageToBeneficiary1","_messageToBeneficiary1"),
      /** 
       * @name messageToBeneficiary2
       * @instance
       * @property get {STRING} - gets the messageToBeneficiary2
       * @property set {STRING} - sets the messageToBeneficiary2
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("messageToBeneficiary2","_messageToBeneficiary2"),
      /** 
       * @name messageToBeneficiary3
       * @instance
       * @property get {STRING} - gets the messageToBeneficiary3
       * @property set {STRING} - sets the messageToBeneficiary3
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("messageToBeneficiary3","_messageToBeneficiary3"),
      /** 
       * @name messageToBeneficiary4
       * @instance
       * @property get {STRING} - gets the messageToBeneficiary4
       * @property set {STRING} - sets the messageToBeneficiary4
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("messageToBeneficiary4","_messageToBeneficiary4"),
      /** 
       * @name reasonForPayment
       * @instance
       * @property get {STRING} - gets the reasonForPayment
       * @property set {STRING} - sets the reasonForPayment
       * @memberof WireActivity
       */
            BCMSRecordProperty.mapToString("reasonForPayment","_reasonForPayment"),
      /** 
       * @name recurringEndByDate
       * @instance
       * @property get {STRING} - gets the recurringEndByDate
       * @property set {STRING} - sets the recurringEndByDate
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToDate("recurringEndByDate","_recurringEndByDate"),
      /** 
       * @name recurringWireDate
       * @instance
       * @property get {STRING} - gets the recurringWireDate
       * @property set {STRING} - sets the recurringWireDate
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToDate("recurringWireDate","_recurringWireDate"),
      /** 
       * @name recurringNumberOfTransfers
       * @instance
       * @property get {STRING} - gets the recurringNumberOfTransfers
       * @property set {STRING} - sets the recurringNumberOfTransfers
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("recurringNumberOfTransfers","_recurringNumberOfTransfers"),
      /** 
       * @name schedulingOption
       * @instance
       * @property get {STRING} - gets the schedulingOption
       * @property set {STRING} - sets the schedulingOption
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("schedulingOption","_schedulingOption"),
      /** 
       * @name frequency
       * @instance
       * @property get {STRING} - gets the frequency
       * @property set {STRING} - sets the frequency
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("frequency","_frequency"),
      /** 
       * @name frequencyType
       * @instance
       * @property get {STRING} - gets the frequencyType
       * @property set {STRING} - sets the frequencyType
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("frequencyType","_frequencyType"),
      /** 
       * @name payeeName
       * @instance
       * @property get {STRING} - gets the payeeName
       * @property set {STRING} - sets the payeeName
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("payeeName","_payeeName"),
           /** 
       * @name companyAccount
       * @instance
       * @property get {STRING} - gets the offsetAccount value
       * @property set {STRING} - sets the offsetAccount value
       * @memberof OneTimeACHPayment
       */
        BCMSRecordProperty.mapToClass("fundingAccount", "_fundingAccount", () => Account),
        /** 
       * @name wireTime
       * @instance
       * @property get {STRING} - gets the wireTime
       * @property set {STRING} - sets the wireTime
       * @memberof WireActivity
       */
      BCMSRecordProperty.mapToString("wireTime","_wireTime"),
  ]
})
